/** A parent source's sub-sources side by side (numbers from the hub endpoint). */
import { Link } from "react-router-dom";
import { GitFork } from "lucide-react";
import { cn } from "@/lib/utils";
import { Section, ShareBar, dotClass, num, usd } from "@/features/command/ui";
import { StatusPill } from "../shared";
import type { ChildSource, HubSource } from "../types";

export function BreakdownSection({ kids, nums, days, from }: {
  kids: ChildSource[];
  nums: Record<string, HubSource> | null;
  days: number;
  from: string;
}) {
  const rows = kids
    .map((k) => ({ k, h: nums?.[k.id] ?? null }))
    .sort((a, b) => (b.h?.n?.revenue ?? 0) - (a.h?.n?.revenue ?? 0) || (b.h?.n?.signups ?? 0) - (a.h?.n?.signups ?? 0));
  const best = Math.max(0, ...rows.map((r) => r.h?.n?.signups ?? 0));
  return (
    <Section title="Inside it" icon={GitFork} count={kids.length} bodyClassName="p-0">
      <div className="hidden grid-cols-[minmax(0,1fr)_4.5rem_4.5rem_5rem_5rem] gap-2 border-b border-zinc-800/70 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 sm:grid">
        <span>Source</span><span className="text-right">Signups</span><span className="text-right">Paying</span><span className="text-right">Revenue</span><span className="text-right">Spend</span>
      </div>
      <ul className="divide-y divide-zinc-800/60">
        {rows.map(({ k, h }) => {
          const n = h?.n;
          return (
            <li key={k.id}>
              <Link
                to={`/traffic/${k.slug}${days !== 30 ? `?days=${days}` : ""}`}
                state={{ from }}
                className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-4 py-2.5 transition-colors hover:bg-zinc-800/30 sm:grid-cols-[minmax(0,1fr)_4.5rem_4.5rem_5rem_5rem]"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={cn("h-2 w-2 shrink-0 rounded-full", dotClass(k.color))} />
                    <span className="truncate text-sm font-medium text-zinc-100">{k.name}</span>
                    <StatusPill status={k.status} className="shrink-0 scale-90" />
                  </div>
                  <ShareBar value={n?.signups ?? 0} max={best} className="mt-1.5 h-1 max-w-[12rem]" />
                </div>
                <span className="text-right text-sm tabular-nums text-zinc-200 sm:hidden">{num(n?.signups)} signups · {usd(n?.revenue)}</span>
                <span className="hidden text-right text-sm tabular-nums text-zinc-200 sm:block">{nums ? num(n?.signups) : "…"}</span>
                <span className="hidden text-right text-sm tabular-nums text-zinc-300 sm:block">{nums ? num(n?.paying) : "…"}</span>
                <span className="hidden text-right text-sm font-semibold tabular-nums text-zinc-100 sm:block">{nums ? usd(n?.revenue) : "…"}</span>
                <span className="hidden text-right text-sm tabular-nums text-zinc-400 sm:block">{nums ? usd(n?.spend) : "…"}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
